<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { TRANSLATION_GROUP_FILE_UPLOAD } from '@modules/forms/constants';
import { useI18n } from '@modules/i18n/composables';
import {
  fileToBase64,
  getImageResolution,
  isImageMimeType,
} from '@utils/file';
import { watchImmediate } from '@vueuse/core';

import type {
  VFormFilePreview,
  VFormFilesInputEmits,
  VFormFilesInputProps,
} from './types';

const props = withDefaults(defineProps<VFormFilesInputProps>(), {
  fileLimit: 100,
  iconType: 'document',
  renameFiles: false,
});
const emit = defineEmits<VFormFilesInputEmits>();

const { getTPath } = useI18n();

const fileInput = ref<HTMLInputElement>();
const hasDrag = ref(false);
const uploadedFiles = ref<VFormFilePreview[]>([]);
const innerValue = computed<File[]>(() => uploadedFiles.value.map((item) => (
  props.renameFiles
    ? new File([item.file], item.name.trim(), { type: item.file.type })
    : item.file
)));
const isMultiple = computed(() => props.fileLimit > 1);
const remainingAllowedFilesCounter = computed(() => props.fileLimit - uploadedFiles.value.length);
const isDisabledByFileLimit = computed(() => uploadedFiles.value.length >= props.fileLimit);
const isChooseDisabled = computed(() => props.disabled || props.readonly || isDisabledByFileLimit.value);
const isUploadDisabled = computed(() => props.disabled || uploadedFiles.value.length === 0);
const localError = ref('');

const uploadIcon = computed(() => {
  if (props.accept?.includes('image')) {
    return 'pi pi-image';
  }

  switch (props.iconType) {
    case 'document':
      return 'pi pi-file';
    case 'image':
      return 'pi pi-image';
    default:
      return 'pi pi-file';
  }
});

function emitChange(): void {
  emit('change', innerValue.value);
}

function onUpload(): void {
  emit('upload', innerValue.value);
  onClear();
}

function onClear(): void {
  uploadedFiles.value = [];
  emitChange();
}

function clearLocalError(): void {
  localError.value = '';
}

function onChoose(): void {
  fileInput.value?.click();
}

function formatBytes(bytes: number, decimals = 3): string {
  if (!+bytes)
    return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

async function toBase64(file: File): Promise<VFormFilePreview> {
  return new Promise((resolve, reject) => {
    if (!isImageMimeType(file)) {
      resolve({
        name: file.name,
        size: formatBytes(file.size),
        file,
      });
      return;
    }

    fileToBase64(file)
      .then((preview) => {
        getImageResolution(preview)
          .then(({ width, height }) => {
            resolve({
              name: file.name,
              size: formatBytes(file.size),
              preview,
              file,
              width,
              height,
            });
          })
          .catch(reject);
      })
      .catch(reject);
  });
}

function filesToUploadedFiles(files: File[] | FileList): Promise<VFormFilePreview[]> {
  const promises = [];

  for (const file of files) {
    promises.push(toBase64(file));
  }

  return Promise.all(promises);
}

async function onFileInputChange(event: Event): Promise<void> {
  onDragLeave();

  if (!fileInput.value?.files?.length)
    return;

  const newFiles = fileInput.value.files;

  // validate files count
  if (newFiles.length > remainingAllowedFilesCounter.value) {
    localError.value = `File limit exceeded. Only ${remainingAllowedFilesCounter.value} more files allowed to upload`;
    event.preventDefault();
    return;
  }

  // validate files' type and size
  for (const file of newFiles) {
    const typeIsInvalid = !isFileTypeValid(file);
    const sizeIsInvalid = !isFileSizeValid(file);

    if (typeIsInvalid) {
      localError.value = props?.invalidTypeLabel || getTPath(TRANSLATION_GROUP_FILE_UPLOAD, 'INVALID_FILE_FORMAT');
    }

    if (sizeIsInvalid) {
      localError.value = props?.invalidSizeLabel || getTPath(TRANSLATION_GROUP_FILE_UPLOAD, 'INVALID_FILE_SIZE');
    }

    if (typeIsInvalid || sizeIsInvalid) {
      fileInput.value.value = '';
      event.preventDefault();
      return;
    }
  }

  try {
    const files = await filesToUploadedFiles(filterFileDuplicates(newFiles));
    uploadedFiles.value = [...uploadedFiles.value, ...files];
  } finally {
    clearLocalError();
    fileInput.value.value = '';
    emitChange();
  }
}

function filterFileDuplicates(files: FileList): File[] {
  const filteredFiles = [];
  for (const file of files) {
    if (!uploadedFiles.value.find((uploadedFile) => uploadedFile.name === file.name)) {
      filteredFiles.push(file);
    }
  }
  return filteredFiles;
}

function isFileSizeValid(file: File): boolean {
  if (!props?.maxSizeBytes)
    return true;

  return file.size <= props.maxSizeBytes;
}

function isFileTypeValid(file: File): boolean {
  if (!props?.accept)
    return true;

  const acceptableTypes = props.accept.split(',').map((type) => type.trim());

  for (const type of acceptableTypes) {
    const acceptable = isTypeWildcard(type)
      ? getTypeClass(file.type) === getTypeClass(type)
      : file.type === type || getFileExtension(file).toLowerCase() === type.toLowerCase();

    if (acceptable) {
      return true;
    }
  }

  return false;
}

function getTypeClass(fileType: string): string {
  return fileType.substring(0, fileType.indexOf('/'));
}

function isTypeWildcard(fileType: string): boolean {
  return fileType.includes('*');
}

function getFileExtension(file: File): string {
  return `.${file.name.split('.').pop()}`;
}

function onDragEnter(): void {
  hasDrag.value = true;
}

function onDragLeave(): void {
  hasDrag.value = false;
}

function clearItem(index: number): void {
  uploadedFiles.value.splice(index, 1);
  emitChange();
}

function onFileNameBlur(uploadedItem: VFormFilePreview): void {
  if (!uploadedItem.name.trim()) {
    uploadedFiles.value = uploadedFiles.value.map((item) => {
      if (item.file.name === uploadedItem.file.name) {
        return {
          ...uploadedItem,
          name: uploadedItem.file.name,
        };
      }
      return item;
    });
  }
  emitChange();
}

watchImmediate(() => props.value, async (newValue) => {
  if (newValue) {
    uploadedFiles.value = await filesToUploadedFiles(newValue);
  } else {
    uploadedFiles.value = [];
  }
}, { deep: true });
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormFilesInput'"
    :errors="errors"
    :hide-errors="hideErrors"
    :readonly="readonly"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
  >
    <label
      v-if="label"
      class="mb-2 block text-muted-color"
    >
      {{ label }}
    </label>
    <div class="rounded-[10px] border">
      <div class="flex flex-1 flex-wrap items-center justify-between gap-2 border-b p-4">
        <div class="flex gap-2">
          <VIconButton
            :icon="uploadIcon"
            :text="false"
            security="primary"
            :disabled="isChooseDisabled"
            @click="onChoose"
          />
          <VButton
            v-if="showUploadButton"
            :label="$getTranslation(TRANSLATION_GROUP_FILE_UPLOAD, 'UPLOAD')"
            icon="pi pi-upload"
            severity="success"
            :disabled="isUploadDisabled"
            @click="onUpload"
          />
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_FILE_UPLOAD, 'CANCEL')"
            icon="pi pi-trash"
            severity="danger"
            :disabled="isUploadDisabled"
            @click="onClear"
          />
        </div>
      </div>
      <div class="min-h-[100px] sm:min-h-[150px]">
        <div class="p-1">
          <VMessage
            v-if="localError"
            severity="error"
            closable
            @close="clearLocalError"
          >
            {{ $t(localError, { format, maxSize: maxSizeBytes ? formatBytes(maxSizeBytes) : undefined }) }}
          </VMessage>
        </div>
        <div
          v-if="!isDisabledByFileLimit"
          class="p-1"
          :class="{
            'p-disabled': isChooseDisabled,
          }"
        >
          <div
            class="relative flex min-h-[100px] flex-col items-center justify-center sm:min-h-[150px]"
            :class="{
              'rounded-xl': hasDrag,
              'border-4': hasDrag,
              'border-dashed': hasDrag,
              'opacity-80': hasDrag,
              'bg-gray-50': hasDrag,
            }"
          >
            <input
              ref="fileInput"
              type="file"
              class="absolute inset-0 opacity-0"
              :name="name"
              :accept="accept"
              :disabled="disabled"
              :readonly="readonly"
              :multiple="isMultiple"
              @dragenter="onDragEnter"
              @dragleave="onDragLeave"
              @change="onFileInputChange"
            >
            <i class="pi pi-cloud-upload text-xl font-normal" />
            <div class="ml-2 text-center">
              <template v-if="dragAndDropLabel">
                {{ dragAndDropLabel }}
              </template>
              <template v-else>
                {{ $getTranslation(TRANSLATION_GROUP_FILE_UPLOAD, 'CHOOSE_FILE', { format }) }}
              </template>
            </div>
          </div>
        </div>
        <div
          v-if="uploadedFiles.length"
          class="grid auto-rows-auto grid-cols-[auto,1.5fr,30px] items-center gap-2 gap-y-4 p-4 sm:grid-cols-[auto,1fr,40px]"
          :class="{
            'border-t': !isChooseDisabled,
          }"
        >
          <template
            v-for="(uploadedFile, index) in uploadedFiles"
            :key="index"
          >
            <div class="m-0">
              <img
                v-if="uploadedFile.preview"
                :src="uploadedFile.preview"
                :width="previewWidth || 50"
                :alt="uploadedFile.name"
              >
            </div>
            <div>
              <div class="mb-1">
                <PInputText
                  v-if="renameFiles"
                  v-model="uploadedFile.name"
                  :class="{ 'p-invalid': !uploadedFile.name?.trim() }"
                  size="small"
                  :pt="{
                    root: {
                      class: 'p-2 w-full',
                    },
                  }"
                  @blur="onFileNameBlur(uploadedFile)"
                />
                <span v-else>{{ uploadedFile.name }}</span>
              </div>
              <div class="text-sm text-muted-color">
                {{ uploadedFile.size }}
                <span
                  v-if="uploadedFile.preview"
                >
                  ({{ uploadedFile.width }}x{{ uploadedFile.height }})
                </span>
              </div>
            </div>
            <div class="text-end">
              <VIconButton
                icon="pi-trash"
                severity="danger"
                @click="clearItem(index)"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </VFormInputWrapper>
</template>
